import { Box, Typography } from '@mui/material';
import { xpCoinSrc } from '../../assets/bazzar';
import { BazaarBackLink, BazaarDialogShell, BazaarRibbonTitle } from './bazaarDialogShared';

/** Figma 9943:26541 — mart voucher details popup */
const DIALOG_MIN_HEIGHT = 520;
const HERO_HEIGHT = 160;
const REDEEM_BUTTON_HEIGHT = 40;

const headerFallbackGradient = {
  cinema: 'linear-gradient(145deg, #0b0205, #2e1500 45%, #0b0205)',
  food: 'linear-gradient(145deg, #4f1300, #d83a00 45%, #f0b135)',
  fashion: 'linear-gradient(145deg, #123d58, #ffffff 48%, #e6006e)',
};

function parseProgress(value = '0%') {
  const match = String(value).match(/(\d+)/);
  const amount = match ? Number(match[1]) : 0;
  return Math.min(Math.max(amount, 0), 100);
}

function MartRedeemButton({ cost, canRedeem, onClick, onInsufficientXp }) {
  const handleClick = () => {
    if (!canRedeem) {
      onInsufficientXp?.();
      return;
    }
    onClick?.();
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      sx={{
        width: '100%',
        maxWidth: '100%',
        height: REDEEM_BUTTON_HEIGHT,
        border: canRedeem ? '1px solid #ffc5f7' : 0,
        borderRadius: '999px',
        p: 0,
        cursor: canRedeem ? 'pointer' : 'not-allowed',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        color: '#fff',
        background: canRedeem
          ? 'linear-gradient(270deg, #ff00f2 0%, #822aff 100%)'
          : 'linear-gradient(90deg, #6b6b6b 0%, #8a8a8a 100%)',
        boxShadow: canRedeem
          ? 'inset 2px 2px 5px rgba(255, 255, 255, 0.35), inset -2px -2px 6px rgba(0, 0, 0, 0.25), 0 4px 0 rgba(0, 0, 0, 0.64)'
          : 'none',
        opacity: canRedeem ? 1 : 0.72,
        fontSize: 15,
        letterSpacing: '0.2px',
      }}
    >
      <Box
        sx={{
          width: 17,
          height: 17,
          borderRadius: '100px',
          overflow: 'hidden',
          flexShrink: 0,
          position: 'relative',
        }}
      >
        <Box
          component="img"
          src={xpCoinSrc}
          alt=""
          sx={{
            position: 'absolute',
            width: '178.09%',
            height: '118.72%',
            maxWidth: 'none',
            left: '-39.86%',
            top: '-8.2%',
          }}
        />
      </Box>
      <Typography
        component="span"
        sx={{
          fontWeight: 500,
          fontSize: 10,
          lineHeight: 1,
          letterSpacing: 0,
          textTransform: 'uppercase',
        }}
      >
        {cost} - Redeem
      </Typography>
    </Box>
  );
}

export default function MartDetailsDialog({ open, card, userXp = 0, onClose, onRedeem, onInsufficientXp }) {
  if (!card) return null;

  const progress = parseProgress(card.progress);
  const requiredXp = Number(card.xpValue) || 0;
  const canRedeem = requiredXp > 0 && userXp >= requiredXp;
  const headerImage = card.imageUrl;
  const headerGradient = headerFallbackGradient[card.imageTone] || headerFallbackGradient.fashion;

  const handleRedeem = () => {
    onRedeem?.(card);
  };

  return (
    <BazaarDialogShell open={open} onClose={onClose} minHeight={DIALOG_MIN_HEIGHT}>
      <BazaarRibbonTitle>{card.brand || 'Voucher Details'}</BazaarRibbonTitle>

      <Box
        sx={{
          width: '100%',
          height: HERO_HEIGHT,
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          mx: 'auto',
        }}
      >
        {headerImage ? (
          <Box
            component="img"
            src={headerImage}
            alt={card.brand || 'Voucher'}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              background: headerGradient,
              display: 'grid',
              placeItems: 'center',
              color: card.brand === 'PVR' ? '#ffc400' : '#fff',
              fontSize: 22,
              fontWeight: 900,
            }}
          >
            {card.brand}
          </Box>
        )}
      </Box>

      <Typography
        sx={{
          mt: 1.8,
          color: '#fff',
          fontWeight: 600,
          fontSize: 16,
          lineHeight: '24px',
          letterSpacing: '0.8px',
          textAlign: 'center',
        }}
      >
        {card.title}
      </Typography>

      {card.subtitle ? (
        <Typography
          sx={{
            mt: 0.6,
            color: '#fff',
            fontWeight: 500,
            fontSize: 14,
            lineHeight: '20px',
            letterSpacing: '0.5px',
            textAlign: 'center',
            opacity: 0.95,
            maxHeight: 200,
            overflowY: 'auto',
            px: 0.5,
          }}
        >
          {card.subtitle}
        </Typography>
      ) : null}

      <Box
        sx={{
          height: 14,
          p: '2px',
          borderRadius: '999px',
          bgcolor: '#d4d4d4',
          boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.35)',
          overflow: 'hidden',
          mt: 2,
          mb: 2.2,
        }}
      >
        <Box
          sx={{
            width: `${progress}%`,
            height: '100%',
            borderRadius: 'inherit',
            background: 'linear-gradient(90deg, #18df08, #03b517)',
          }}
        />
      </Box>

      <MartRedeemButton
        cost={card.cost}
        canRedeem={canRedeem}
        onClick={handleRedeem}
        onInsufficientXp={onInsufficientXp}
      />

      <BazaarBackLink onClick={onClose} />
    </BazaarDialogShell>
  );
}
